import styles from './ServiceIcons.module.scss';
import { ServiceIcon, ServiceIconSize } from '../ServiceIcon';
import { ServiceIconNumeric } from './ServiceIconNumeric';

export type Service = {
  name: string;
  src: string;
};

type ServiceIconsProps<T extends Service> = {
  /** Label to render for screenreaders. */
  'aria-label'?: string;
  /**
   * Maximum number of service icons to render. If more are provided, the final "icon" will be a number representing the number of unrendered services.
   */
  maxServices?: number;
  /**
   * An array of service icons to render.
   *
   * - `name` is the name of the service whose icon is being displayed (used as alt text for the icon), e.g., "Google Sheets".
   * - `src` is a URL for the service icon image to be rendered. If this is an Imgix URL, a large-format image can be used and the image will automatically be resized.
   */
  services: Array<T>;
  /**
   * Determines the size of every shell and icon.
   *
   * `size` maps to the following shell sizes:
   * - xx-small: 16x16
   * - x-small: 20x20
   * - small (20): 24x24
   * - medium (40): 36x36
   * - large (60): 48x48
   */
  size?: ServiceIconSize;
  /**
   * Controls the presence/absence of the border.
   * It is recommended that a borderless ServiceIcon be contained in a container with non primary color background.
   */
  hasBorder?: boolean;
  /** Function to override the default rendering of each service icon. */
  renderServiceIcon?: (
    service: T,
    props: ServiceIconsProps<T>
  ) => React.ReactNode;
  /** Changes the background color to white (#fff). */
  hasWhiteBackground?: boolean;
  /** Indicates whether to render the component as an inline element (when `false`) or block element (when `true`). */
  isBlock?: boolean;
};

/**
 * Renders an icon representing a service (also known as an app or integration).
 *
 * The aesthetics of this component are intended to convey that the services are related.
 */
export const ServiceIcons = <T extends Service>({
  'aria-label': ariaLabel,
  hasBorder = true,
  hasWhiteBackground = false,
  maxServices = 6,
  renderServiceIcon,
  services,
  size = 'medium',
  isBlock,
}: ServiceIconsProps<T>) => {
  // Restructuring props because renderServiceIcon type definition requires all props to be passed as the second argument. This avoids a breaking change.
  const props = {
    'aria-label': ariaLabel,
    hasBorder,
    services,
    hasWhiteBackground,
    maxServices,
    renderServiceIcon,
    size,
    isBlock,
  };

  const label = ariaLabel || `${services.length} apps mentioned`;

  const hasExtraServices = services.length > maxServices;

  // +1 to account for the extra hidden icon to make space for the +X block.
  const numServicesHidden = hasExtraServices
    ? services.length - maxServices + 1
    : 0;
  // -1 to account for the extra hidden icon to make space for the +X block.
  const numServicesShown = hasExtraServices ? maxServices - 1 : services.length;

  // Guarantee that we render the first and last service in the provided list.
  // Prefer showing the first service if that's all there's room for.
  let visibleServices: Array<T> = [];

  if (numServicesShown === 1) {
    visibleServices = [services[0]];
  } else if (numServicesShown >= 2) {
    visibleServices = [
      ...services.slice(0, numServicesShown - 1),
      services[services.length - 1],
    ];
  }

  // Using `idx` as `key` since the same `service` could potentially
  // appear multiple times and therefore would not be unique.
  const icons = visibleServices.map((service, idx) => (
    <li key={idx} data-zds>
      {renderServiceIcon ? (
        renderServiceIcon(service, props)
      ) : (
        <ServiceIcon
          hasBorder={hasBorder}
          hasWhiteBackground={hasWhiteBackground}
          serviceName={service.name}
          size={size}
          src={service.src}
        />
      )}
    </li>
  ));

  if (hasExtraServices) {
    const hiddenServicesCountIcon = (
      <li key={`remainder-${numServicesHidden}`} data-zds>
        <ServiceIconNumeric
          size={size}
          number={numServicesHidden}
          hasBorder={hasBorder}
          hasWhiteBackground={hasWhiteBackground}
        />
      </li>
    );

    if (maxServices > 2) icons.splice(-1, 0, hiddenServicesCountIcon);
    else icons.push(hiddenServicesCountIcon);
  }

  return (
    <ul
      aria-label={label}
      className={styles.serviceIcons}
      data-is-block={isBlock}
      data-zds
    >
      {icons}
    </ul>
  );
};
